import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TeethSelector from './TeethSelector';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import { apiClient } from '../../services/apiClient';
import jsPDF from 'jspdf';
import medidentVsSsBg1 from '../../assets/medident-vs-ss-fr-1.png';
import medidentVsSsBg2 from '../../assets/medident-vs-ss-fr-2.png';
import colors from '../../config/colors';
import { normalizeTeethListFields, parseTeethList } from '../../utils/teethList';
import EuropeanDateInput from '../common/EuropeanDateInput';

export const medidentVsSsDefaultFormData = {
  'centre-medico-social': '',
  'medecin-dentiste': '',
  'nom-prenom': '',
  'date-naissance': '',
  adresse: '',
  'commune-domicile': '',
  'date-premiere-consultation': '',
  'cas-lamal-opas': 'non',
  'cas-accident': 'non',
  'prestations-complementaires': 'non',
  'missing-teeth': '',
  'defective-teeth': '',
  'repaired-teeth': '',
  'periodontal-teeth': '',
  'present-chart-teeth': '',
  'prosthetic-description': '',
  'proposition-traitement': '',
  'lieu-date': '',
};

const MEDIDENT_IMG_W = 1191;
const MEDIDENT_IMG_H = 1684;

// Odontogram digit centers (image px), measured from medident-vs-ss-fr-1.png.
// Layout: 18..11 | 21..28 / 48..41 | 31..38
const presentToothCenters = {
  18: [584, 1468], 17: [612, 1468], 16: [640, 1468], 15: [668, 1468], 14: [695, 1468], 13: [722, 1468], 12: [750, 1468], 11: [778, 1468],
  21: [881, 1468], 22: [910, 1468], 23: [938, 1468], 24: [964, 1468], 25: [993, 1468], 26: [1020, 1468], 27: [1048, 1468], 28: [1076, 1468],
  48: [584, 1498], 47: [612, 1498], 46: [640, 1498], 45: [668, 1498], 44: [695, 1498], 43: [722, 1498], 42: [750, 1498], 41: [778, 1498],
  31: [881, 1498], 32: [910, 1498], 33: [938, 1498], 34: [964, 1498], 35: [993, 1498], 36: [1020, 1498], 37: [1048, 1498], 38: [1076, 1498],
};

// Status table: same FDI columns as odontogram, shifted to the grid center bar (x≈828).
const ODO_CENTER_X = (presentToothCenters[11][0] + presentToothCenters[21][0]) / 2;
const STATUS_GRID_CENTER_X = 828;
const STATUS_GRID_X_OFFSET = STATUS_GRID_CENTER_X - ODO_CENTER_X;

const DECIDUOUS_TO_PERMANENT = {
  55: 15, 54: 14, 53: 13, 52: 12, 51: 11,
  61: 21, 62: 22, 63: 23, 64: 24, 65: 25,
  85: 45, 84: 44, 83: 43, 82: 42, 81: 41,
  71: 31, 72: 32, 73: 33, 74: 34, 75: 35,
};

// Status rows (image px): upper/lower half of each grid band for upper/lower teeth.
const statusRowY = {
  'missing-teeth': { upperY: 1155, lowerY: 1187 },
  'defective-teeth': { upperY: 1219, lowerY: 1251 },
  'repaired-teeth': { upperY: 1284, lowerY: 1316 },
  'periodontal-teeth': { upperY: 1348, lowerY: 1380 },
};

// Oui/non checkbox centers — labels from OCR, boxes aligned under Q3 measured squares.
const ouiNonMarks = {
  'cas-lamal-opas': { oui: [596, 817], non: [674, 817] },
  'cas-accident': { oui: [596, 894], non: [674, 894] },
  'prestations-complementaires': { oui: [598, 1050], non: [676, 1050] },
};

const isUpperTooth = (tooth) => {
  const n = Number(tooth);
  // Permanent 1x/2x + deciduous 5x/6x (upper); 3x/4x + 7x/8x are lower.
  return (n >= 11 && n <= 28) || (n >= 51 && n <= 65);
};

const statusToothX = (tooth) => {
  const n = Number(tooth);
  const mapped = DECIDUOUS_TO_PERMANENT[n] || n;
  const pos = presentToothCenters[mapped];
  if (!pos) return null;
  return pos[0] + STATUS_GRID_X_OFFSET;
};

function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Place wrapped text on fixed baselines. If content exceeds the row capacity,
 * split into 2 or 3 columns (same approach as MedidentNe wrapTextMultiColumn).
 */
const drawTextOnBaselinesMultiColumn = (
  doc,
  text,
  baselines,
  xImg,
  maxWidthImg,
  sx,
  sy,
  fontSize = 8,
  { gapImg = 16 } = {}
) => {
  if (!text) return;
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);

  const maxRows = baselines.length;
  const gap = gapImg * sx;
  const maxWidth = maxWidthImg * sx;
  const colWidthFor = (cols) => (maxWidth - gap * (cols - 1)) / cols;
  const linesIfCols = (cols) => doc.splitTextToSize(String(text), colWidthFor(cols));
  const maxLinesIfCols = (cols) => Math.ceil(linesIfCols(cols).length / cols);

  let cols = 1;
  if (maxLinesIfCols(1) > maxRows) {
    cols = maxLinesIfCols(2) <= maxRows ? 2 : 3;
  }

  const colWidth = colWidthFor(cols);
  const lines = linesIfCols(cols);
  const perCol = Math.ceil(lines.length / cols);

  for (let c = 0; c < cols; c++) {
    const colLines = lines.slice(c * perCol, (c + 1) * perCol);
    const colX = xImg * sx + c * (colWidth + gap);
    colLines.forEach((line, i) => {
      if (i < maxRows) doc.text(line, colX, baselines[i] * sy);
    });
  }
};

// Section 2: 2 writing traits ≈1596/1624; 2 text lines under each (offset enough to avoid strike-through)
const PROSTHETIC_BASELINES = [1610, 1622, 1638, 1650];
// Section 3: 2 writing traits ≈162/225; 2 text lines just above each (close but not struck)
const PROPOSITION_BASELINES = [144, 156, 207, 219];

const MedidentVsSsForm = ({ initialData, onSave }) => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState(() => ({
    ...medidentVsSsDefaultFormData,
    ...normalizeTeethListFields(initialData || {}),
  }));

  useEffect(() => {
    setFormData({
      ...medidentVsSsDefaultFormData,
      ...normalizeTeethListFields(initialData || {}),
    });
  }, [initialData]);

  const debouncedSave = useRef(
    debounce((data) => {
      if (typeof onSave === 'function') onSave(data);
    }, 500)
  ).current;

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      debouncedSave(next);
      return next;
    });
  };

  const isFieldEmpty = (field) => {
    const value = formData[field];
    if (typeof value === 'boolean') return !value;
    return value == null || String(value).trim() === '';
  };

  const isSectionComplete = ({ sectionFields = [], requireAtLeastOne = false, isOptional = false }) => {
    if (!sectionFields.length) return false;
    if (requireAtLeastOne || isOptional) return sectionFields.some((field) => !isFieldEmpty(field));
    return sectionFields.every((field) => !isFieldEmpty(field));
  };

  const renderAccordionSection = ({ title, children, sectionFields = [], isOptional = false, requireAtLeastOne = false }) => {
    const hasEmptyMandatory = !isOptional && (
      requireAtLeastOne
        ? !sectionFields.some((field) => !isFieldEmpty(field))
        : sectionFields.some((field) => isFieldEmpty(field))
    );
    const showFilled = isSectionComplete({ sectionFields, requireAtLeastOne, isOptional });
    return (
      <Disclosure>
        {({ open }) => (
          <div className={`mb-4 rounded-lg ${open ? 'bg-white shadow-md' : ''}`}>
            <Disclosure.Button className="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium bg-white text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-50">
              <span>{title}</span>
              <div className="flex items-center gap-2">
                {!isOptional && hasEmptyMandatory ? (
                  <span className="px-2 py-1 text-xs font-medium bg-red-50 text-red-600 rounded">{t('insurance.labels.required')}</span>
                ) : showFilled ? (
                  <span className="px-2 py-1 text-xs font-medium bg-green-50 text-green-600 rounded">{t('insurance.labels.filled')}</span>
                ) : null}
                <ChevronUpIcon className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-gray-500 ml-2`} />
              </div>
            </Disclosure.Button>
            <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-600">{children}</Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    );
  };

  const renderField = (field, label, type = 'text', required = false) => (
    <div className="flex flex-col space-y-1">
      {label && (
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {type === 'textarea' ? (
        <textarea
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          rows={4}
          className="rounded-md shadow-sm border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 mt-1 block w-full"
        />
      ) : type === 'date' ? (
        <EuropeanDateInput value={formData[field] || ''} onChange={(value) => handleChange(field, value)} required={required} />
      ) : (
        <input
          type={type}
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="rounded-md shadow-sm border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
        />
      )}
    </div>
  );

  const renderOuiNon = (field, label, required = false) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
        {label}
        {required && <span className="text-red-500">*</span>}
      </span>
      <div className="flex items-center gap-4">
        {['oui', 'non'].map((opt) => (
          <label key={opt} className="inline-flex items-center gap-1 text-sm cursor-pointer">
            <input
              type="radio"
              name={field}
              checked={formData[field] === opt}
              onChange={() => handleChange(field, opt)}
              className="h-4 w-4 border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );

  const scale = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    return { sx: pageWidth / MEDIDENT_IMG_W, sy: pageHeight / MEDIDENT_IMG_H, pageWidth, pageHeight };
  };

  const drawMarkX = (doc, ix, iy, fontSize = 10) => {
    const { sx, sy } = scale(doc);
    doc.setFontSize(fontSize);
    doc.setTextColor(0, 0, 0);
    doc.text('X', ix * sx, iy * sy, { align: 'center', baseline: 'middle' });
  };

  const drawCircle = (doc, ix, iy, rImg = 10) => {
    const { sx, sy } = scale(doc);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1.2);
    doc.circle(ix * sx, iy * sy, rImg * sx, 'S');
  };

  const handleGeneratePDF = async () => {
    if (typeof onSave === 'function') onSave(formData);

    const doc = new jsPDF({ unit: 'px', format: 'a4' });
    const getImageBase64 = (src) =>
      new Promise((resolve) => {
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.onload = function () {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          canvas.getContext('2d').drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = src;
      });

    const [bg1, bg2] = await Promise.all([getImageBase64(medidentVsSsBg1), getImageBase64(medidentVsSsBg2)]);

    let signatureDataUrl = null;
    let signatureType = 'JPEG';
    try {
      const profile = await apiClient.get('/api/profile');
      if (profile?.signature_image) {
        signatureDataUrl = profile.signature_image;
        signatureType = signatureDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      }
    } catch (e) {
      // ignore
    }

    const { sx, sy, pageWidth, pageHeight } = scale(doc);
    doc.addImage(bg1, 'PNG', 0, 0, pageWidth, pageHeight);
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    // Text baselines sit just above each underline (OCR colon ends → underline starts).
    const page1Fields = [
      ['centre-medico-social', 312, 472],
      ['medecin-dentiste', 295, 500],
      ['nom-prenom', 230, 540],
      ['date-naissance', 280, 584],
      ['adresse', 170, 624],
      ['commune-domicile', 325, 664],
      ['date-premiere-consultation', 435, 728],
    ];
    page1Fields.forEach(([key, ix, iy]) => {
      if (formData[key]) doc.text(String(formData[key]), ix * sx, iy * sy);
    });

    Object.entries(ouiNonMarks).forEach(([field, marks]) => {
      const isOui = String(formData[field]).toLowerCase() === 'oui';
      const [mx, my] = isOui ? marks.oui : marks.non;
      drawMarkX(doc, mx, my, 11);
    });

    // Status grids: FDI in odontogram columns (shifted to center bar).
    // Deciduous (>50) overlay permanent columns: 55–51→15–11, 61–65→21–25, 85–81→45–41, 71–75→31–35.
    Object.entries(statusRowY).forEach(([field, cfg]) => {
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      parseTeethList(formData[field]).forEach((tooth) => {
        const n = Number(tooth);
        const mapped = DECIDUOUS_TO_PERMANENT[n] || n;
        const x = statusToothX(tooth);
        if (x == null) return;
        const y = isUpperTooth(mapped) ? cfg.upperY : cfg.lowerY;
        doc.text(String(tooth), x * sx, y * sy, { align: 'center', baseline: 'middle' });
      });
    });

    // Present odontogram: deciduous share permanent digit columns.
    parseTeethList(formData['present-chart-teeth']).forEach((tooth) => {
      const n = Number(tooth);
      const mapped = DECIDUOUS_TO_PERMANENT[n] || n;
      const pos = presentToothCenters[mapped];
      if (pos) drawCircle(doc, pos[0], pos[1], 11);
    });

    drawTextOnBaselinesMultiColumn(
      doc,
      formData['prosthetic-description'],
      PROSTHETIC_BASELINES,
      100,
      980,
      sx,
      sy,
      8
    );

    // Page 2
    doc.addPage();
    doc.addImage(bg2, 'PNG', 0, 0, pageWidth, pageHeight);
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    drawTextOnBaselinesMultiColumn(
      doc,
      formData['proposition-traitement'],
      PROPOSITION_BASELINES,
      100,
      990,
      sx,
      sy,
      8
    );

    // Mask devis zone: keep « 4. DEVIS » title, cover instructions + full table width
    const devisMaskX = 50;
    const devisMaskY = 300;
    const devisMaskW = 1110;
    const devisMaskH = 900;
    doc.setFillColor(255, 255, 255);
    doc.rect(devisMaskX * sx, devisMaskY * sy, devisMaskW * sx, devisMaskH * sy, 'F');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(
      t('medidentVsSs.fields.attachedQuoteLabel'),
      (devisMaskX + devisMaskW / 2) * sx,
      (devisMaskY + devisMaskH / 2) * sy,
      { align: 'center', baseline: 'middle' }
    );

    if (formData['lieu-date']) {
      doc.setFontSize(9);
      doc.text(String(formData['lieu-date']), 200 * sx, 1245 * sy);
    }

    if (signatureDataUrl) {
      try {
        // Inside the dashed « Sceau et signature » frame (top ≈ 1289)
        doc.addImage(signatureDataUrl, signatureType, 760 * sx, 1305 * sy, 180 * sx, 55 * sy);
      } catch (e) {
        // ignore
      }
    }

    doc.save('medident-valais-ss-form.pdf');
  };

  const ADMIN_FIELDS = [
    'centre-medico-social',
    'medecin-dentiste',
    'nom-prenom',
    'date-naissance',
    'adresse',
    'commune-domicile',
    'date-premiere-consultation',
  ];
  const CONTEXT_FIELDS = ['cas-lamal-opas', 'cas-accident', 'prestations-complementaires'];
  const TEETH_FIELDS = [
    'missing-teeth',
    'defective-teeth',
    'repaired-teeth',
    'periodontal-teeth',
    'present-chart-teeth',
    'prosthetic-description',
  ];

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{t('consultation.steps.medidentVsSs')}</h2>
        <button
          type="button"
          onClick={handleGeneratePDF}
          style={{
            backgroundColor: colors.primary.selected,
            color: 'white',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            fontWeight: 500,
            border: 'none',
          }}
        >
          {t('insurance.buttons.generatePdf', 'Generate PDF')}
        </button>
      </div>

      {renderAccordionSection({
        title: t('medidentVsSs.sections.administrative'),
        sectionFields: ADMIN_FIELDS,
        children: (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {renderField('centre-medico-social', t('medidentVsSs.fields.centreMedicoSocial'))}
            {renderField('medecin-dentiste', t('medidentVsSs.fields.medecinDentiste'), 'text', true)}
            {renderField('nom-prenom', t('medidentVsSs.fields.nomPrenom'), 'text', true)}
            {renderField('date-naissance', t('medidentVsSs.fields.dateNaissance'), 'date', true)}
            {renderField('adresse', t('medidentVsSs.fields.adresse'), 'text', true)}
            {renderField('commune-domicile', t('medidentVsSs.fields.communeDomicile'))}
            {renderField('date-premiere-consultation', t('medidentVsSs.fields.datePremiereConsultation'), 'date')}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentVsSs.sections.context'),
        sectionFields: CONTEXT_FIELDS,
        children: (
          <div className="space-y-4">
            {renderOuiNon('cas-lamal-opas', t('medidentVsSs.fields.casLamalOpas'), true)}
            {renderOuiNon('cas-accident', t('medidentVsSs.fields.casAccident'), true)}
            {renderOuiNon('prestations-complementaires', t('medidentVsSs.fields.prestationsComplementaires'), true)}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentVsSs.sections.teeth'),
        sectionFields: TEETH_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-6">
            {[
              ['missing-teeth', 'missingTeeth'],
              ['defective-teeth', 'defectiveTeeth'],
              ['repaired-teeth', 'repairedTeeth'],
              ['periodontal-teeth', 'periodontalTeeth'],
            ].map(([field, labelKey]) => (
              <div key={field}>
                <p className="text-sm font-medium text-gray-700 mb-2">{t(`medidentVsSs.fields.${labelKey}`)}</p>
                <TeethSelector
                  selectedTeeth={formData[field] || ''}
                  onTeethSelect={(value) => handleChange(field, value)}
                />
              </div>
            ))}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">{t('medidentVsSs.fields.presentChartTeeth')}</p>
              <TeethSelector
                selectedTeeth={formData['present-chart-teeth'] || ''}
                onTeethSelect={(value) => handleChange('present-chart-teeth', value)}
              />
            </div>
            {renderField('prosthetic-description', t('medidentVsSs.fields.prostheticDescription'), 'textarea')}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentVsSs.sections.treatment'),
        sectionFields: ['proposition-traitement', 'lieu-date'],
        isOptional: true,
        children: (
          <div className="space-y-4">
            {renderField('proposition-traitement', t('medidentVsSs.fields.propositionTraitement'), 'textarea')}
            {renderField('lieu-date', t('medidentVsSs.fields.lieuDate'))}
            <p className="text-xs text-gray-500">{t('medidentVsSs.fields.devisMaskedHint')}</p>
          </div>
        ),
      })}
    </form>
  );
};

export default MedidentVsSsForm;
